import React, { useEffect, useRef, useState } from 'react';
import { MAX_CELL_INITIALS_LENGTH } from '../constants/bookingConst';
// Sanitised on every keystroke, not only on commit, so the admin sees
// exactly what will be saved as they type.
import { sanitizeInitials } from '../utils/bookingUtils';

/**
 * The small left-hand box in a "Manage availability" grid cell, in its
 * editable form: an admin types a walk-in's initials straight into an open
 * hour instead of opening the full booking modal for it.
 *
 * Commits on BLUR, not on change. The status flip the commit triggers
 * re-renders the cell, so committing per keystroke would tear the field out
 * from under the second character. Enter commits by blurring; Escape
 * abandons the edit and restores the last committed value.
 *
 * Controlled from `value` but buffered in local state, because the parent
 * only learns the new initials at commit time and the field has to stay
 * typeable until then.
 *
 * @param {object} props
 * @param {string} props.value - Committed initials ('' when the hour is free).
 * @param {(initials: string) => void} props.onCommit - Called on blur with the
 *   sanitized value, only when it differs from `value`.
 * @param {() => void} props.onOpenModal - Double-click handler, for the full
 *   Book New Slot form.
 * @param {string} [props.title] - Tooltip text.
 * @returns {JSX.Element}
 */
const CellInitialsInput = ({ value, onCommit, onOpenModal, title }) => {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  // Set by Escape so the blur it causes commits nothing.
  const abandonedRef = useRef(false);

  // Re-syncs when the committed value changes from outside the field — a
  // Clear changes press, or the grid reloading the date underneath it.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleBlur = () => {
    if (abandonedRef.current) {
      abandonedRef.current = false;
      return;
    }
    const cleaned = sanitizeInitials(draft);
    // Guarded so tabbing through an untouched grid stages nothing.
    if (cleaned !== value) onCommit(cleaned);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      inputRef.current?.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      abandonedRef.current = true;
      setDraft(value);
      inputRef.current?.blur();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      className="ats-initials-input"
      value={draft}
      placeholder="--"
      maxLength={MAX_CELL_INITIALS_LENGTH}
      inputMode="text"
      autoComplete="off"
      title={title}
      onChange={(e) => setDraft(sanitizeInitials(e.target.value))}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onDoubleClick={onOpenModal}
    />
  );
};

export default CellInitialsInput;
